// apps/cop/src/host/cert.ts
// CONTRACT:I5-KEY-ACCESS.1.0
//
// COP — DEMO client-side cert holder. Opens the per-user encrypted cert envelope
// (fetched fail-soft AFTER a successful /idp/token login) and holds the decrypted
// signing key + PEMs IN MEMORY ONLY. This is the operator's PKI identity the COP
// will sign with; it is NEVER persisted (no sessionStorage/localStorage) so a tab
// close or logout (clearCert) drops it.
//
// DEMO ENVELOPE (PBKDF2-HMAC-SHA256 → AES-256-GCM, WebCrypto-only — mirrors
// packages/signed-entitlement/src/crypto.ts's subtle()-only style):
//   { v, demo, alg, kdf:{name,hash,iterations,salt(b64)}, iv(b64), aad(=username),
//     cn, not_after, ct(b64 of ciphertext||16B GCM tag) }
//   inner plaintext (after decrypt) → { key(PKCS#8 PEM), cert(PEM), ca(PEM), cn }
//
// FAIL-CLOSED: a wrong password / tampered blob / cross-user envelope (the AAD is
// the username) THROWS at decrypt (GCM auth) — the caller (tdflite.login) catches
// and enters the COP in a "no cert" state. This module never half-loads: on any
// failure it throws and leaves prior state untouched / cleared.

/** The decrypted, in-memory cert identity. `signingKey` is a NON-EXTRACTABLE holder key. */
export interface CertHandle {
  cn: string;
  certPem: string;
  caPem: string;
  signingKey: CryptoKey;
  notAfter: string;
}

/** The DEMO cert envelope wire shape (encrypted). */
interface CertEnvelope {
  v?: number;
  demo?: boolean;
  alg?: string;
  kdf?: { name?: string; hash?: string; iterations?: number; salt?: string };
  iv?: string;
  aad?: string;
  cn?: string;
  not_after?: string;
  ct?: string;
}

/** The inner plaintext shape (after AES-GCM open). */
interface CertInner {
  key: string;
  cert: string;
  ca: string;
  cn?: string;
}

// Module-held state — IN MEMORY ONLY, never serialized. null = no cert loaded.
let state: CertHandle | null = null;

function subtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (!c || !c.subtle) throw new Error("WebCrypto subtle is unavailable in this runtime");
  return c.subtle;
}

/** A standalone ArrayBuffer copy of a byte view — the WebCrypto BufferSource params want a
 *  concrete ArrayBuffer (not the ArrayBufferLike union the lib types infer). */
function ab(bytes: Uint8Array): ArrayBuffer {
  return bytes.slice().buffer as ArrayBuffer;
}

function utf8(s: string): ArrayBuffer {
  return ab(new TextEncoder().encode(s));
}

/** Decode standard base64 → bytes (exact-size buffer, so `.buffer` is the whole value). */
function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Strip a PKCS#8 PEM header/footer and base64-decode the body to DER bytes. */
function pemToDer(pem: string): Uint8Array {
  const body = pem
    .replace(/-----BEGIN [^-]+-----/g, "")
    .replace(/-----END [^-]+-----/g, "")
    .replace(/\s+/g, "");
  return b64ToBytes(body);
}

/** Open the encrypted cert envelope AS `username` with `password`, holding the decrypted
 *  identity in memory. THROWS on any decrypt/parse failure (fail-closed — the caller catches
 *  and enters the COP without a cert). Replaces any previously-held cert on success. */
export async function loadCertFromEnvelope(
  envelopeJson: CertEnvelope | string,
  password: string,
  username: string,
): Promise<void> {
  const env: CertEnvelope =
    typeof envelopeJson === "string" ? (JSON.parse(envelopeJson) as CertEnvelope) : envelopeJson;

  if (!env || !env.kdf || !env.kdf.salt || !env.iv || !env.ct) {
    throw new Error("malformed cert envelope (missing kdf.salt / iv / ct)");
  }

  const salt = b64ToBytes(env.kdf.salt);
  const iv = b64ToBytes(env.iv);
  const ct = b64ToBytes(env.ct); // ciphertext WITH the appended 16-byte GCM tag

  const baseKey = await subtle().importKey("raw", utf8(password), "PBKDF2", false, ["deriveKey"]);
  const aesKey = await subtle().deriveKey(
    {
      name: "PBKDF2",
      salt: ab(salt),
      iterations: env.kdf.iterations ?? 600000,
      hash: env.kdf.hash ?? "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"],
  );

  // The AAD is the USERNAME — a cross-user envelope fails the GCM auth check here (throws).
  const plainBuf = await subtle().decrypt(
    { name: "AES-GCM", iv: ab(iv), additionalData: utf8(username), tagLength: 128 },
    aesKey,
    ab(ct),
  );

  const inner = JSON.parse(new TextDecoder().decode(new Uint8Array(plainBuf))) as CertInner;
  if (!inner || typeof inner.key !== "string" || typeof inner.cert !== "string") {
    throw new Error("malformed cert plaintext (missing key / cert)");
  }

  const der = pemToDer(inner.key);
  // Non-extractable holder key — the COP can sign with it but can never read it back out.
  const signingKey = await subtle().importKey(
    "pkcs8",
    ab(der),
    { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
    false,
    ["sign"],
  );

  state = {
    cn: inner.cn ?? env.cn ?? "",
    certPem: inner.cert,
    caPem: inner.ca,
    signingKey,
    notAfter: env.not_after ?? "",
  };
}

/** The in-memory cert identity, or null if none is loaded. */
export function getCert(): CertHandle | null {
  return state;
}

/** True iff a cert is currently held in memory. */
export function hasCert(): boolean {
  return state !== null;
}

/** Drop the held cert: zeroize the PEM strings (in place, so any holder of the handle sees
 *  them blanked) and drop the signing-key ref. Called on logout + persona switch. */
export function clearCert(): void {
  if (state) {
    state.certPem = "";
    state.caPem = "";
    state.cn = "";
    state.notAfter = "";
  }
  state = null; // drops the non-extractable signingKey ref
}
